import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import { ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useGetCardSetConfig } from '@sorare/core/src/set';

import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { Sport } from '@sorare/core/src/__generated__/globalTypes.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper/index.tsx';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';
import type { ClaimStarterPack_cardSetRoot } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  position: relative;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 4 })`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--triple-unit);
  justify-content: space-between;

  @media ${laptopAndAbove} {
    margin: calc(6 * var(--unit)) 0;
    gap: 104px;
    justify-content: center;
  }
`;

const CountdownLabel = styled(BrandXXL)`
  font-variant-numeric: tabular-nums;
`;

const CountdownCaption = styled(BrandXS)`
  color: var(--c-nd-600);
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  margin-top: auto;
  margin-bottom: calc(10 * var(--unit));

  &.disabled {
    backdrop-filter: blur(32px);
    border-radius: var(--unit);
  }

  &.no-top-margin {
    margin-top: 0;
  }

  @media ${laptopAndAbove} {
    margin-top: 0;
  }
`;

const PlayersLeft = styled.img`
  position: absolute;
  left: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PlayersRight = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PlayersMobile = styled.img`
  position: absolute;
  left: 50%;
  bottom: calc(10 * var(--unit));
  max-width: var(--100vw);
  max-height: calc(var(--100vh) - calc(40 * var(--unit)));
  transform: translateX(-50%);
`;

const PacksImage = styled.img`
  height: 240px;
  max-width: var(--100vw);
`;

type Props = { cardSetRoot: ClaimStarterPack_cardSetRoot; refetch: () => void };
export const ClaimStarterPack = ({ cardSetRoot, refetch }: Props) => {
  const getSetConfig = useGetCardSetConfig();
  const { generateSportContextPath } = useSportContext();
  const { currentSet, nextSet } = cardSetRoot;
  const isLaptopAndAbove = useIsLaptop();
  const { sport } = useSportContext();
  const isSupportedSport = sport !== Sport.BASEBALL;

  const showTimer = !currentSet && !!nextSet?.startDate;
  const setSlug = showTimer ? nextSet?.slug : currentSet?.slug;

  const setConfig = getSetConfig(setSlug);

  const { visual, background } = setConfig;
  const { brand, seasonCountdown } = visual;
  const { logoImage } = brand;
  const showAssets = !!seasonCountdown && isSupportedSport;
  const showPacksImage = showAssets && !isLaptopAndAbove && !showTimer;
  const showPlayersDesktop = showAssets && isLaptopAndAbove;
  const showPlayersMobile = showAssets && !isLaptopAndAbove && showTimer;

  return (
    <Root>
      <Background
        style={
          seasonCountdown
            ? isLaptopAndAbove
              ? seasonCountdown.backgroundDesktop
              : seasonCountdown.backgroundMobile
            : background
        }
      />
      {!!showPlayersDesktop && (
        <>
          <PlayersLeft
            src={seasonCountdown.players[sport].desktopLeft}
            alt=""
          />
          <PlayersRight
            src={seasonCountdown.players[sport].desktopRight}
            alt=""
          />
        </>
      )}
      {!!showPlayersMobile && (
        <PlayersMobile src={seasonCountdown.players[sport].mobile} alt="" />
      )}
      <Content gap={1.5} center>
        <ConditionalWrapper
          wrap={!isLaptopAndAbove}
          Wrapper={Vertical}
          props={{ center: true, gap: 4 }}
        >
          <img src={logoImage} alt="" height={48} />
          {showTimer ? (
            <Vertical center gap={1.5}>
              <BrandS>
                <FormattedMessage defaultMessage="begins in" />
              </BrandS>
              <Countdown
                date={nextSet.startDate}
                Label={CountdownLabel}
                Caption={CountdownCaption}
                gap={3}
                onEnded={refetch}
              />
            </Vertical>
          ) : (
            <>
              <BrandXXL
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color': 'var(--c-gradient-common)',
                  } as CSSProperties
                }
              >
                <FormattedMessage defaultMessage="Starts now" />
              </BrandXXL>
            </>
          )}
        </ConditionalWrapper>
        {!!showPacksImage && (
          <PacksImage src={seasonCountdown.packs[sport]} alt="" />
        )}
        <CTAWrapper
          className={classNames({
            disabled: !currentSet,
            'no-top-margin': showPacksImage || isLaptopAndAbove,
          })}
        >
          <Button
            size="large"
            color="primary"
            to={generateSportContextPath(
              ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION
            )}
            fullWidth
            disabled={!currentSet}
          >
            <FormattedMessage defaultMessage="Claim my starter pack" />
          </Button>
        </CTAWrapper>
      </Content>
      <AbsoluteNav />
    </Root>
  );
};

ClaimStarterPack.fragments = {
  cardSetRoot: gql`
    fragment ClaimStarterPack_cardSetRoot on CardSetRoot {
      id
      currentSet {
        slug
        name
      }
      nextSet {
        slug
        name
        startDate
      }
    }
  ` as TypedDocumentNode<ClaimStarterPack_cardSetRoot>,
};
